import clsx from 'clsx'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement } from 'react'
import styled from 'styled-components'
import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getSearchTileDensityClassName } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { _searchTileContainerQueryUp } from './_SearchTileBase'

const compactPricingLayout = `
  flex-direction: row;
  gap: ${rem(12)};
  align-items: end;
`

const defaultPricingLayout = `
  flex-direction: column;
  gap: ${rem(8)};
  align-items: stretch;
`

const Container = styled.div`
  grid-area: pricing;
  display: flex;
  flex-direction: row;
  gap: ${rem(12)};
  align-items: end;

  &.density-compact {
    justify-content: space-between;
  }

  &.density-cro-compact {
    justify-content: flex-end;
  }

  ${_searchTileContainerQueryUp.tablet} {
    ${defaultPricingLayout}

    &.density-compact,
    &.density-cro-compact {
      ${compactPricingLayout}
    }

    &.density-cro-compact {
      justify-content: flex-end;
    }
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    ${compactPricingLayout}
  }

  ${_searchTileContainerQueryUp.desktop} {
    ${defaultPricingLayout}

    &.density-compact,
    &.density-cro-compact {
      ${compactPricingLayout}
    }

    &.density-cro-compact {
      justify-content: flex-end;
    }
  }
`

const Details = styled.div`
  flex-grow: 1;
  min-width: 0;

  &.density-cro-compact {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: end;
  }

  ${_searchTileContainerQueryUp.tablet} {
    &:not(.density-compact):not(.density-cro-compact) {
      min-width: ${rem(200)};
    }
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    min-width: 0;
  }

  ${_searchTileContainerQueryUp.desktop} {
    &:not(.density-compact):not(.density-cro-compact) {
      min-width: ${rem(200)};
    }
  }
`

const Action = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  min-width: fit-content;
  max-width: ${rem(200)};

  &.density-cro-compact {
    min-width: 0;
  }

  ${_searchTileContainerQueryUp.tablet} {
    &:not(.density-compact):not(.density-cro-compact) {
      flex-grow: 1;
    }
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    flex-grow: 0;
  }

  ${_searchTileContainerQueryUp.desktop} {
    &:not(.density-compact):not(.density-cro-compact) {
      flex-grow: 1;
    }
  }

  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren {
  action: ReactElement | false
  /**
   * @default default
   */
  density?: SearchTileDensity
}

function _SearchTilePricing({ children, action, density = 'default' }: Props) {
  const densityClassName = getSearchTileDensityClassName(density)

  return <Container className={clsx(_SearchTilePricing.name, densityClassName)}>
    <Details className={densityClassName}>{children}</Details>
    {!!action && <Action className={densityClassName}>{action}</Action>}
  </Container>
}

export default _SearchTilePricing
